import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import Select from 'react-select';
import {
  getAllStates,
  getVillagesByBlocks,
  districts as masterDistrictsData
} from '../shared/Constants/locations';

const cookies = new Cookies();

// Build reverse lookup of district -> state so district names mistakenly saved in state can be normalized
const districtToStateMap = {};
if (masterDistrictsData && typeof masterDistrictsData === 'object') {
  for (const [st, dList] of Object.entries(masterDistrictsData)) {
    if (Array.isArray(dList)) {
      for (const d of dList) {
        if (d && d.name) {
          districtToStateMap[d.name.toLowerCase().trim()] = st;
        }
      }
    }
  }
}

// Helper to get auth token
const getAuthToken = () => {
  try {
    const raw = cookies.get('access_token') || localStorage.getItem('humblebeeAuthToken');
    if (!raw) return 'humblbee_admin_token';
    if (typeof raw === 'string' && (raw.startsWith('{') || raw.startsWith('"'))) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') return parsed;
      return parsed?.token || parsed?.access_token || raw;
    }
    return raw;
  } catch (e) {
    return 'humblbee_admin_token';
  }
};

// Safe string extractor for location values (State, District, Block, Village)
// Prevents any crash from null, undefined, numbers, or MongoDB objects
export const getSafeLocationString = (val) => {
  if (!val) return '';
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed === 'null' || trimmed === 'undefined' || trimmed === '[object Object]') return '';
    return trimmed;
  }
  if (typeof val === 'number') return String(val).trim();
  if (typeof val === 'object' && val !== null) {
    let candidate = '';
    try {
      if (typeof val?.name === 'string') candidate = val.name;
      else if (typeof val?.village_name === 'string') candidate = val.village_name;
      else if (typeof val?.label === 'string') candidate = val.label;
      else if (typeof val?.value === 'string') candidate = val.value;
      else if (typeof val?.title === 'string') candidate = val.title;
    } catch {
      candidate = '';
    }
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed === 'null' || trimmed === 'undefined' || trimmed === '[object Object]') return '';
      return trimmed;
    }
    return '';
  }
  return '';
};

// Safe extractor for all 4 location tiers from any trainee item
export const extractLocation = (lead) => {
  if (!lead || typeof lead !== 'object') {
    return { state: '', district: '', block: '', village: '' };
  }
  const uAddr = (lead.userInfo && typeof lead.userInfo.address === 'object' && lead.userInfo.address !== null)
    ? lead.userInfo.address
    : {};
  const fData = (lead.surveyData?.stages?.[0]?.form_data && typeof lead.surveyData.stages[0].form_data === 'object')
    ? lead.surveyData.stages[0].form_data
    : {};

  let state = getSafeLocationString(uAddr.state) || getSafeLocationString(fData.state) || getSafeLocationString(lead.state) || '';
  let district = getSafeLocationString(uAddr.district) || getSafeLocationString(fData.district) || getSafeLocationString(lead.district) || '';
  let block = getSafeLocationString(uAddr.block) || getSafeLocationString(fData.block) || getSafeLocationString(lead.block) || '';
  let village = getSafeLocationString(uAddr.village) || getSafeLocationString(fData.village) || getSafeLocationString(lead.village_name) || getSafeLocationString(lead.village) || '';

  // If state was mistakenly saved as a district name (e.g. Dantewada, Darjeeling, Deoria, Srikakulam, Tamenglong, Kodagu, Prayagraj, etc.)
  const stateLower = state.toLowerCase().trim();
  if (districtToStateMap[stateLower]) {
    const parentState = districtToStateMap[stateLower];
    if (!district || district.toLowerCase().trim() === stateLower) {
      district = state;
    }
    state = parentState;
  }

  // If district was given but state was missing, infer state from district
  const districtLower = district.toLowerCase().trim();
  if (!state && districtToStateMap[districtLower]) {
    state = districtToStateMap[districtLower];
  }

  return { state, district, block, village };
};

// Always retrieve guaranteed safe location object without property collisions
export const getLeadLocation = (lead) => {
  const loc = (lead?.safeLocation && typeof lead.safeLocation === 'object')
    ? lead.safeLocation
    : extractLocation(lead);

  return {
    state: typeof loc?.state === 'string' ? loc.state : '',
    district: typeof loc?.district === 'string' ? loc.district : '',
    block: typeof loc?.block === 'string' ? loc.block : '',
    village: typeof loc?.village === 'string' ? loc.village : '',
  };
};

// Helper: Format Dates cleanly
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const m = moment(dateStr);
    return m.isValid() ? m.format('DD/MM/YYYY hh:mm A') : '-';
  } catch {
    return '-';
  }
};

// Helper: Calculate Age from DOB
const calculateAge = (dobVal) => {
  if (!dobVal) return '-';
  try {
    const dob = new Date(dobVal);
    if (isNaN(dob.getTime())) return '-';
    const diffMs = Date.now() - dob.getTime();
    const ageDt = new Date(diffMs);
    const age = Math.abs(ageDt.getUTCFullYear() - 1970);
    return isNaN(age) || age <= 0 || age > 120 ? '-' : age;
  } catch {
    return '-';
  }
};

const BulkAssignTrainees = () => {
  const history = useHistory();

  // Core Data States
  const [loading, setLoading] = useState(false);
  const [allLeads, setAllLeads] = useState([]); // Master raw dataset
  const [projectsList, setProjectsList] = useState([]);
  const [clustersList, setClustersList] = useState([]);

  // Assignment Controls & Modal States
  const [targetProjectId, setTargetProjectId] = useState('');
  const [targetClusterId, setTargetClusterId] = useState('');
  const [showConfirmAssignModal, setShowConfirmAssignModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignStep, setAssignStep] = useState('idle'); // 'idle' | 'processing' | 'done' | 'partial' | 'error'
  const [assignProgress, setAssignProgress] = useState(0);
  const [assignCurrent, setAssignCurrent] = useState(0);
  const [assignTotal, setAssignTotal] = useState(0);
  const [assignCurrentName, setAssignCurrentName] = useState('');
  const [assignTargetNames, setAssignTargetNames] = useState({ project: '', cluster: '' });
  const [assignResults, setAssignResults] = useState({
    successCount: 0,
    failCount: 0,
    totalCount: 0,
    errorDetails: [],
  });

  // Selected Lead IDs for Bulk Assignment
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Search & Filter States
  const [searchText, setSearchText] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [quickViewUser, setQuickViewUser] = useState(null);

  // Filter Modal Form Values (Matching Leads.js)
  const [filters, setFilters] = useState({
    state: '',
    district: '',
    block: '',
    village: [],
    projectName: '',
    clusterStatus: 'all', // 'all' | 'assigned' | 'unassigned'
    gender: '',
    srlm_associated: '',
    apiaryStatus: '',
    stage: '', // 'all' | 'advance' | 'basic'
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // 1. Data Fetching & Combination Logic (Identical to Leads.js)
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());

    const token = getAuthToken();
    const bearerHeader = `Bearer ${token}`;

    try {
      // A. Fetch Basic Profiles
      let basicProfiles = [];
      try {
        const bpRes = await axios.get(
          `${process.env.REACT_APP_API_APP_FIELD_URL}/surveys/bp/admin-base-profile`,
          {
            headers: {
              source: 'humblebee_admin',
              Authorization: bearerHeader,
            },
          }
        );
        basicProfiles = bpRes?.data?.data || [];
      } catch (bpErr) {
        console.warn('Basic profiles fetch with user token failed, retrying with admin token:', bpErr);
        try {
          const bpFallback = await axios.get(
            `${process.env.REACT_APP_API_APP_FIELD_URL}/surveys/bp/admin-base-profile`,
            {
              headers: {
                source: 'humblebee_admin',
                Authorization: 'Bearer humblbee_admin_token',
              },
            }
          );
          basicProfiles = bpFallback?.data?.data || [];
        } catch (fErr) {
          console.error('Basic profiles fetch error:', fErr);
        }
      }

      // B. Fetch Baseline Survey Data (Advance Profiles)
      let baselineData = [];
      try {
        const baseRes = await fetch(
          `${process.env.REACT_APP_API_APP_USER_URL}/user/getAllSurveyedUsers?limit=2000&role=trainee_beekeeper`,
          {
            method: 'GET',
            headers: {
              source: 'humblebee_admin',
              authorization: 'Bearer humblbee_admin_token',
            },
          }
        );
        const baseJson = await baseRes.json();
        baselineData = baseJson?.userInfo || [];
      } catch (baseErr) {
        console.error('Baseline survey fetch error:', baseErr);
      }

      // C. Fetch Clusters
      let fetchedClusters = [];
      try {
        const clusterRes = await axios.get(
          `${process.env.REACT_APP_HUMBLEBEE_API_URL}/projects/clusters/getAll`,
          {
            headers: {
              authorization: bearerHeader,
            },
          }
        );
        fetchedClusters = clusterRes?.data?.clusters || [];
      } catch (cErr) {
        console.warn('Cluster fetch error:', cErr);
        // Fallback endpoint
        try {
          const altClusterRes = await axios.get(
            `${process.env.REACT_APP_API_APP_FIELD_URL}/clusters/clusters`,
            {
              headers: {
                source: 'humblebee_admin',
                authorization: bearerHeader,
              },
            }
          );
          fetchedClusters = altClusterRes?.data?.data || [];
        } catch (altErr) {
          console.error('Alt cluster fetch error:', altErr);
        }
      }
      setClustersList(fetchedClusters);

      // D. Fetch Projects
      let fetchedProjects = [];
      try {
        const projRes = await axios.get(
          `${process.env.REACT_APP_HUMBLEBEE_API_URL}/projects/`,
          {
            headers: {
              authorization: bearerHeader,
            },
          }
        );
        fetchedProjects = projRes?.data?.projects || [];
      } catch (pErr) {
        console.error('Projects fetch error:', pErr);
      }
      setProjectsList(fetchedProjects);

      // E. Combine Data using Leads.js algorithm
      const combined = [];
      const seenUserIds = new Set();
      const seenMobiles = new Set();

      // 1. Process baseline survey data (stage = "advance")
      if (baselineData && baselineData.length) {
        baselineData.forEach((item, idx) => {
          const userId = item.userInfo?._id || item._id;
          const mobile = item.userInfo?.mobileNumber
            ? item.userInfo.mobileNumber.toString().replace(/\D/g, '').slice(-10)
            : '';

          const isDuplicateUser = userId && seenUserIds.has(userId.toString());
          const isDuplicateMobile = mobile && mobile !== '0000000000' && seenMobiles.has(mobile);

          if (isDuplicateUser || isDuplicateMobile) return;

          if (userId) seenUserIds.add(userId.toString());
          if (mobile && mobile !== '0000000000') seenMobiles.add(mobile);

          const uniqueKey = userId ? userId.toString() : `advance_${idx}_${Date.now()}`;

          combined.push({
            ...item,
            stage: 'advance',
            surveyorName: item.surveyData?.created_by?.name || null,
            _id: uniqueKey,
            id: uniqueKey,
            rawUserId: userId,
            aadhar_number: item.aadhar_number || null,
            srlm_associated: item.srlm_associated || false,
            userInfo: {
              ...item.userInfo,
              address: {
                ...item.userInfo?.address,
                village: item.userInfo?.address?.village || null,
                block: item.userInfo?.address?.block || null,
                logged_in: item.userInfo?.logged_in || false,
                addedAt: item.userInfo?.addedAt || item.userInfo?.created_at,
              },
            },
          });
        });
      }

      // 2. Process basic profiles (stage = "basic")
      if (basicProfiles && basicProfiles.length) {
        basicProfiles.forEach((profile, idx) => {
          const mobile = profile.mobile_number
            ? profile.mobile_number.toString().replace(/\D/g, '').slice(-10)
            : '';
          const userId = profile._id || profile.id;

          const existsAsAdvance = combined.some(
            x =>
              (mobile &&
                x.userInfo?.mobileNumber?.toString().replace(/\D/g, '').slice(-10) === mobile) ||
              (userId &&
                (x._id === userId.toString() || x.userInfo?._id?.toString() === userId.toString()))
          );

          if (!existsAsAdvance) {
            const uniqueKey = userId ? userId.toString() : `basic_${idx}_${Date.now()}`;
            if (userId) seenUserIds.add(userId.toString());
            if (mobile && mobile !== '0000000000') seenMobiles.add(mobile);

            combined.push({
              _id: uniqueKey,
              id: uniqueKey,
              rawUserId: userId,
              stage: 'basic',
              surveyorName: profile.created_by_user?.name || null,
              userInfo: {
                _id: profile._id,
                name: profile.full_name,
                mobileNumber: profile.mobile_number,
                gender: profile.gender,
                address: {
                  state: profile.state,
                  district: profile.district,
                  village: profile.village_name,
                  block: profile.block,
                  logged_in: profile.logged_in,
                  addedAt: profile.created_at,
                },
              },
              fieldViewData: [
                {
                  created_at: profile.created_at,
                  updated_at: profile.updated_at,
                  cluster_id: profile.cluster_id,
                },
              ],
              surveyData: {
                stages: [
                  {
                    form_data: {
                      dob: profile.dob,
                    },
                  },
                ],
              },
              aadhar_number: profile.aadhar_number || null,
              srlm_associated: profile.srlm_associated || false,
              clusterName: null,
              projectName: null,
            });
          }
        });
      }

      // Trim address fields
      const cleaned = combined.map(item => {
        const address = item.userInfo?.address || {};
        return {
          ...item,
          userInfo: {
            ...item.userInfo,
            address: {
              ...address,
              village: typeof address.village === 'string' ? address.village.trim() : '',
              block: typeof address.block === 'string' ? address.block.trim() : '',
            },
          },
        };
      });

      // Map cluster names, project names, project IDs, and extract clean safe location
      const mapped = cleaned.map(item => {
        // Sort fieldViewData by newest updated_at or created_at
        const fvList = Array.isArray(item?.fieldViewData) ? [...item.fieldViewData] : [];
        fvList.sort((a, b) => new Date(b?.updated_at || b?.created_at || 0) - new Date(a?.updated_at || a?.created_at || 0));
        const latestFv = fvList[0] || {};

        // Comprehensive cluster ID resolution
        // Checking userInfo.cluster first ensures changes made via attachLeadToCluster reflect immediately
        const clusterId =
          item?.userInfo?.cluster ||
          latestFv.cluster_id ||
          item?.cluster_id ||
          item?.clusterId ||
          null;

        // Comprehensive project ID resolution
        const projectId =
          item?.userInfo?.projectId ||
          item?.userInfo?.adminMetaInfo?.projectId ||
          latestFv.project_id ||
          item?.projectId ||
          null;

        const matchedCluster = fetchedClusters.find(c =>
          String(c._id) === String(clusterId) ||
          String(c.id) === String(clusterId)
        );
        const safeLocation = extractLocation(item);

        return {
          ...item,
          safeLocation,
          clusterId,
          projectId,
          clusterName: matchedCluster?.clusterName || item.clusterName || null,
          projectName: matchedCluster?.projectId?.name || matchedCluster?.projectName || item.projectName || null,
        };
      });

      // Sort by updated_at descending
      mapped.sort((a, b) => {
        return (
          new Date(b.fieldViewData?.[0]?.updated_at || b.userInfo?.addedAt || 0) -
          new Date(a.fieldViewData?.[0]?.updated_at || a.userInfo?.addedAt || 0)
        );
      });

      console.log('📊 [BulkAssignTrainees] Loaded all data successfully:', {
        total: mapped.length,
        basic: mapped.filter(x => x.stage === 'basic').length,
        advance: mapped.filter(x => x.stage === 'advance').length,
        assigned: mapped.filter(x => x.clusterName).length,
        unassigned: mapped.filter(x => !x.clusterName).length,
        clustersInDatabase: fetchedClusters.length,
        projectsInDatabase: fetchedProjects.length,
      });

      setAllLeads(mapped);
    } catch (err) {
      console.error('Error combining leads data:', err);
      toast.error('Failed to load lead profiles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Dynamic Location Lists derived safely from allLeads
  const locationOptions = useMemo(() => {
    const statesSet = new Set();
    const districtsSet = new Set();
    const blocksSet = new Set();
    const villagesSet = new Set();

    const filterState = String(filters.state || '').trim().toLowerCase();
    const filterDistrict = String(filters.district || '').trim().toLowerCase();
    const filterBlock = String(filters.block || '').trim().toLowerCase();

    // Canonical list of all Indian states
    const masterStates = getAllStates();
    const validStatesMap = new Map();
    masterStates.forEach(s => validStatesMap.set(s.toLowerCase(), s));

    (allLeads || []).forEach(lead => {
      const loc = getLeadLocation(lead);
      const uState = String(loc.state || '').trim();
      const uDistrict = String(loc.district || '').trim();
      const uBlock = String(loc.block || '').trim();
      const uVillage = String(loc.village || '').trim();

      // Only add legitimate states from canonical Indian states (no district names)
      if (uState) {
        const canonical = validStatesMap.get(uState.toLowerCase());
        if (canonical) {
          statesSet.add(canonical);
        }
      }

      const stateMatch = !filterState || uState.toLowerCase() === filterState;
      if (stateMatch) {
        if (uDistrict) districtsSet.add(uDistrict);

        const districtMatch = !filterDistrict || uDistrict.toLowerCase() === filterDistrict;
        if (districtMatch) {
          if (uBlock) blocksSet.add(uBlock);

          const blockMatch = !filterBlock || uBlock.toLowerCase() === filterBlock;
          if (blockMatch) {
            if (uVillage) villagesSet.add(uVillage);
          }
        }
      }
    });

    // Also include master villages for the chosen district so users can filter by any village in the district
    if (filters.district) {
      try {
        const masterVillages = getVillagesByBlocks(filters.district, filters.block || []);
        if (Array.isArray(masterVillages)) {
          masterVillages.forEach(v => {
            if (v && typeof v === 'string') villagesSet.add(v.trim());
          });
        }
      } catch (e) {
        console.warn('Error fetching master villages:', e);
      }
    }

    return {
      states: Array.from(statesSet).filter(Boolean).sort(),
      districts: Array.from(districtsSet).filter(Boolean).sort(),
      blocks: Array.from(blocksSet).filter(Boolean).sort(),
      villages: Array.from(villagesSet).filter(Boolean).sort(),
    };
  }, [allLeads, filters.state, filters.district, filters.block]);

  // Derived Filtered Leads
  const filteredLeads = useMemo(() => {
    let result = Array.isArray(allLeads) ? allLeads : [];

    // 1. Text Search (Search by Name / Mobile / Aadhaar / Village / Block / District / State)
    if (searchText && typeof searchText === 'string' && searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      result = result.filter(item => {
        const loc = getLeadLocation(item);
        const name = String(item?.userInfo?.name || '').toLowerCase();
        const mobile = String(item?.userInfo?.mobileNumber || '');
        const aadhar = String(item?.aadhar_number || '');
        const village = String(loc.village || '').toLowerCase();
        const block = String(loc.block || '').toLowerCase();
        const district = String(loc.district || '').toLowerCase();
        const state = String(loc.state || '').toLowerCase();
        const surveyor = String(item?.surveyorName || '').toLowerCase();
        return (
          name.includes(q) ||
          mobile.includes(q) ||
          aadhar.includes(q) ||
          village.includes(q) ||
          block.includes(q) ||
          district.includes(q) ||
          state.includes(q) ||
          surveyor.includes(q)
        );
      });
    }

    // 2. Stage Filter ('advance' | 'basic')
    if (filters.stage) {
      result = result.filter(item => item?.stage === filters.stage);
    }

    // 3. State Filter
    if (filters.state) {
      const targetState = String(filters.state || '').trim().toLowerCase();
      result = result.filter(item => {
        const loc = getLeadLocation(item);
        return String(loc.state || '').toLowerCase() === targetState;
      });
    }

    // 4. District Filter
    if (filters.district) {
      const targetDistrict = String(filters.district || '').trim().toLowerCase();
      result = result.filter(item => {
        const loc = getLeadLocation(item);
        return String(loc.district || '').toLowerCase() === targetDistrict;
      });
    }

    // 5. Block Filter
    if (filters.block) {
      const targetBlock = String(filters.block || '').trim().toLowerCase();
      result = result.filter(item => {
        const loc = getLeadLocation(item);
        return String(loc.block || '').toLowerCase() === targetBlock;
      });
    }

    // 6. Village Filter (Support multi-select array like Lead Profile or single string)
    if (filters.village) {
      const isArr = Array.isArray(filters.village);
      const hasSelection = isArr ? filters.village.length > 0 : Boolean(String(filters.village).trim());
      if (hasSelection) {
        const targetVillages = isArr
          ? filters.village.map(v => String(typeof v === 'object' ? v?.value || v?.label : v).trim().toLowerCase()).filter(Boolean)
          : [String(filters.village).trim().toLowerCase()];

        if (targetVillages.length > 0) {
          result = result.filter(item => {
            const loc = getLeadLocation(item);
            const v = String(loc.village || '').trim().toLowerCase();
            return targetVillages.includes(v);
          });
        }
      }
    }

    // 7. Project Name Filter
    if (filters.projectName) {
      result = result.filter(item => {
        return item?.projectName === filters.projectName;
      });
    }

    // 8. Cluster Status Filter ('all' | 'assigned' | 'unassigned')
    if (filters.clusterStatus === 'assigned') {
      result = result.filter(item => Boolean(item?.clusterName));
    } else if (filters.clusterStatus === 'unassigned') {
      result = result.filter(item => !item?.clusterName);
    }

    // 9. Gender Filter
    if (filters.gender) {
      const targetGender = String(filters.gender || '').trim().toLowerCase();
      result = result.filter(item => {
        const g = String(item?.userInfo?.gender || '').trim().toLowerCase();
        return g === targetGender;
      });
    }

    // 10. SRLM Associated Filter
    if (filters.srlm_associated !== '') {
      const isSrlm = filters.srlm_associated === 'true';
      result = result.filter(item => Boolean(item?.srlm_associated) === isSrlm);
    }

    // 11. Apiary Status Filter
    if (filters.apiaryStatus) {
      result = result.filter(item => item?.apiaryStatus === filters.apiaryStatus);
    }

    return result;
  }, [allLeads, searchText, filters]);

  // Overall Counts for Stat Chips (Calculated accurately from current dataset)
  const stats = useMemo(() => {
    const total = allLeads.length;
    const basic = allLeads.filter(x => x.stage === 'basic').length;
    const advance = allLeads.filter(x => x.stage === 'advance').length;
    const assigned = allLeads.filter(x => Boolean(x.clusterName)).length;
    const unassigned = total - assigned;

    return { total, basic, advance, assigned, unassigned };
  }, [allLeads]);

  // Available clusters for selected target project
  const availableTargetClusters = useMemo(() => {
    if (!targetProjectId) return [];
    return clustersList.filter(c => {
      const pId = c.projectId?._id || c.projectId || c.project_id;
      return String(pId) === String(targetProjectId);
    });
  }, [clustersList, targetProjectId]);

  // Paginated Rows
  const paginatedLeads = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredLeads.slice(startIndex, startIndex + pageSize);
  }, [filteredLeads, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredLeads.length / pageSize) || 1;

  // Selection Handlers
  const handleToggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      paginatedLeads.forEach(lead => next.add(lead._id));
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    const allFilteredIds = new Set(filteredLeads.map(l => l._id));
    setSelectedIds(allFilteredIds);
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const isAllPageSelected = useMemo(() => {
    if (paginatedLeads.length === 0) return false;
    return paginatedLeads.every(lead => selectedIds.has(lead._id));
  }, [paginatedLeads, selectedIds]);

  // Handle Opening Confirm Modal
  const handleAssignToCluster = () => {
    if (selectedIds.size === 0) {
      toast.warning('Please select at least one trainee to assign.');
      return;
    }
    if (!targetProjectId) {
      toast.warning('Please select a Target Project.');
      return;
    }
    if (!targetClusterId) {
      toast.warning('Please select a Target Cluster.');
      return;
    }

    const selectedProjectObj = projectsList.find(p => String(p._id) === String(targetProjectId));
    const selectedClusterObj = clustersList.find(c => String(c._id) === String(targetClusterId));

    const projName = selectedProjectObj?.name || 'Selected Project';
    const clustName = selectedClusterObj?.clusterName || 'Selected Cluster';

    setAssignTargetNames({ project: projName, cluster: clustName });
    setShowConfirmAssignModal(true);
  };

  // Execute Bulk Assignment with Live Feedback
  const executeBulkAssign = async () => {
    setShowConfirmAssignModal(false);
    setIsAssigning(true);
    setAssignStep('processing');

    const idsToAssign = Array.from(selectedIds);
    const total = idsToAssign.length;
    setAssignTotal(total);
    setAssignCurrent(0);
    setAssignProgress(0);
    setAssignCurrentName('');

    console.log('🚀 [BulkAssign] Starting bulk assignment process:', {
      totalCandidates: total,
      targetProjectId,
      targetClusterId,
      assignTargetNames,
    });

    const token = getAuthToken();
    const config = {
      headers: {
        'Content-Type': 'application/json',
        source: 'humblebee_admin',
        Authorization: 'Bearer ' + (token || 'humblbee_admin_token'),
      },
    };

    let successCount = 0;
    let failCount = 0;
    const assignedUserIds = new Set();
    const errorDetails = [];

    for (let i = 0; i < idsToAssign.length; i++) {
      const uId = idsToAssign[i];
      const targetItem = allLeads.find(l => l._id === uId);
      const actualUserId =
        targetItem?.userInfo?._id || targetItem?.rawUserId || targetItem?._id || uId;
      const candidateName =
        targetItem?.userInfo?.name || targetItem?.name || `Trainee #${i + 1}`;

      setAssignCurrentName(candidateName);
      setAssignCurrent(i + 1);
      const pct = Math.round(((i + 1) / total) * 100);
      setAssignProgress(pct);

      console.log(`🔍 [BulkAssign] (${i + 1}/${total}) Processing Candidate:`, {
        name: candidateName,
        rowId: uId,
        actualUserId,
        stage: targetItem?.stage,
        currentCluster: targetItem?.clusterName || 'None',
        newClusterId: targetClusterId,
        newClusterName: assignTargetNames.cluster,
        newProjectId: targetProjectId,
        newProjectName: assignTargetNames.project,
      });

      let assignedSuccessfully = false;
      let lastErrorMessage = '';

      // 1. Path A: Basic Profile sync (ONLY for basic profiles)
      if (targetItem?.stage === 'basic' && targetItem?.userInfo?.mobileNumber) {
        try {
          const bpPayload = {
            mobile_number: targetItem?.userInfo?.mobileNumber,
            cluster_id: targetClusterId,
            project_id: targetProjectId,
          };
          console.log(`📡 [BulkAssign] Syncing base profile for ${candidateName}:`, bpPayload);
          const bpSyncRes = await axios.post(
            `${process.env.REACT_APP_API_APP_FIELD_URL}/surveys/bp/base-profile`,
            bpPayload,
            config
          );
          console.log(`📥 [BulkAssign] Base profile response for ${candidateName}:`, bpSyncRes.status, bpSyncRes.data);
          if (bpSyncRes.status === 200 || bpSyncRes.status === 201 || bpSyncRes.data?.status) {
            assignedSuccessfully = true;
          }
        } catch (bpErr) {
          console.warn(`⚠️ [BulkAssign] Base profile sync note for ${candidateName}:`, bpErr?.response?.data || bpErr?.message);
        }
      }

      // 2. Path B: Call /clusters/clusters/add-lead (exact endpoint used by individual profile)
      try {
        const payload = {
          user_id: actualUserId,
          cluster_id: targetClusterId,
          project_id: targetProjectId,
        };

        const surveyId = targetItem?.surveyData?._id || targetItem?.fieldViewData?.[0]?.survey_id;
        if (surveyId && /^[0-9a-fA-F]{24}$/.test(String(surveyId))) {
          payload.survey_id = String(surveyId);
        }

        console.log(`📡 [BulkAssign] Calling /clusters/clusters/add-lead for ${candidateName}:`, payload);
        const response = await axios.post(
          `${process.env.REACT_APP_API_APP_FIELD_URL}/clusters/clusters/add-lead`,
          payload,
          config
        );

        console.log(`✅ [BulkAssign] add-lead API response for ${candidateName}:`, {
          status: response.status,
          data: response.data,
        });

        if (response.data?.status || response.status === 200 || response.status === 201) {
          assignedSuccessfully = true;
        } else if (!assignedSuccessfully) {
          lastErrorMessage = response.data?.message || 'Server returned unfulfilled assignment';
        }
      } catch (err) {
        const errMsg = err?.response?.data?.message || err?.message || 'Network / server error';
        console.error(`❌ [BulkAssign] Failed to assign user ${actualUserId} (${candidateName}):`, {
          status: err?.response?.status,
          error: errMsg,
          details: err?.response?.data,
        });
        if (!assignedSuccessfully) {
          lastErrorMessage = errMsg;
        }
      }

      if (assignedSuccessfully) {
        successCount++;
        assignedUserIds.add(uId);
        console.log(`✨ [BulkAssign] Successfully assigned ${candidateName} to ${assignTargetNames.cluster}!`);
      } else {
        failCount++;
        errorDetails.push(`${candidateName}: ${lastErrorMessage}`);
      }
    }

    // Immediately update in-memory state so table rows reflect the change
    if (assignedUserIds.size > 0) {
      console.log(`💾 [BulkAssign] Updating in-memory state for ${assignedUserIds.size} trainee(s)...`);
      setAllLeads(prev =>
        prev.map(item => {
          if (assignedUserIds.has(item._id)) {
            const updatedFv = Array.isArray(item.fieldViewData) ? [...item.fieldViewData] : [];
            const newFvEntry = {
              ...(updatedFv[0] || {}),
              cluster_id: targetClusterId,
              project_id: targetProjectId,
              updated_at: new Date().toISOString(),
            };
            return {
              ...item,
              clusterId: targetClusterId,
              projectId: targetProjectId,
              clusterName: assignTargetNames.cluster,
              projectName: assignTargetNames.project,
              userInfo: {
                ...(item.userInfo || {}),
                cluster: targetClusterId,
                projectId: targetProjectId,
              },
              fieldViewData: [newFvEntry, ...updatedFv.slice(1)],
            };
          }
          return item;
        })
      );
    }

    setSelectedIds(new Set());
    setAssignResults({
      successCount,
      failCount,
      totalCount: total,
      errorDetails,
    });

    console.log('🏁 [BulkAssign] Batch assignment complete:', {
      total,
      successCount,
      failCount,
      errorDetails,
    });

    if (failCount === 0) {
      setAssignStep('done');
      toast.success(`${successCount} trainee(s) assigned to ${assignTargetNames.cluster}!`);
    } else if (successCount > 0) {
      setAssignStep('partial');
      toast.warning(`${successCount} assigned, ${failCount} failed.`);
    } else {
      setAssignStep('error');
      toast.error('Bulk assignment failed.');
    }
  };

  // Close Assignment Modal & Refresh Data
  const handleCloseAssignModal = async () => {
    setIsAssigning(false);
    setAssignStep('idle');
    try {
      await fetchAllData();
    } catch (refErr) {
      console.warn('Post-assignment refresh note:', refErr);
    }
  };

  // Export CSV Handler
  const handleExportCSV = () => {
    if (filteredLeads.length === 0) {
      toast.info('No data to export.');
      return;
    }

    const headers = [
      'S.No.',
      'Name',
      'Surveyed By',
      'Stage',
      'Gender',
      'Age',
      'Mobile No.',
      'State',
      'District',
      'Village',
      'Block',
      'Aadhaar Number',
      'SRLM Associated',
      'Apiary',
      'Project Name',
      'Cluster Name',
    ];

    const rows = filteredLeads.map((item, idx) => {
      const u = item.userInfo || {};
      const addr = u.address || {};
      const dobVal = item.surveyData?.stages?.[0]?.form_data?.dob;
      const age = calculateAge(dobVal);

      return [
        idx + 1,
        `"${u.name || '-'}"`,
        `"${item.surveyorName || '-'}"`,
        item.stage === 'advance' ? 'Advanced' : 'Basic',
        u.gender || '-',
        age,
        u.mobileNumber || '-',
        `"${getLeadLocation(item).state || '-'}"`,
        `"${getLeadLocation(item).district || '-'}"`,
        `"${getLeadLocation(item).village || '-'}"`,
        `"${getLeadLocation(item).block || '-'}"`,
        `"${item.aadhar_number || '-'}"`,
        item.srlm_associated ? 'Yes' : 'No',
        item.apiaryStatus || 'Absent',
        `"${item.projectName || 'No Cluster Assigned'}"`,
        `"${item.clusterName || 'No Cluster Assigned'}"`,
      ];
    });

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `leads_trainees_${moment().format('YYYYMMDD_HHmm')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchText('');
    setFilters({
      state: '',
      district: '',
      block: '',
      village: [],
      projectName: '',
      clusterStatus: 'all',
      gender: '',
      srlm_associated: '',
      apiaryStatus: '',
      stage: '',
    });
    setCurrentPage(1);
  };

  // Active filter count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.state) count++;
    if (filters.district) count++;
    if (filters.block) count++;
    if (filters.village && (Array.isArray(filters.village) ? filters.village.length > 0 : Boolean(filters.village))) count++;
    if (filters.projectName) count++;
    if (filters.clusterStatus !== 'all') count++;
    if (filters.gender) count++;
    if (filters.srlm_associated !== '') count++;
    if (filters.apiaryStatus) count++;
    if (filters.stage) count++;
    return count;
  }, [filters]);

  return (
    <div
      style={{
        marginLeft: '15%',
        marginTop: '5%',
        width: '85%',
        minHeight: '95vh',
        padding: '24px 30px',
        boxSizing: 'border-box',
        backgroundColor: '#f8f9fa',
        fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
      }}
    >
      {/* Top Header / Breadcrumb */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div>
          <div style={{ fontSize: '12px', color: '#636e72', marginBottom: '4px' }}>
            <Link to="/clusters" style={{ color: '#ff8701', textDecoration: 'none' }}>
              &larr; Back to Clusters
            </Link>{' '}
            / Bulk Assign Trainees
          </div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#2d3436' }}>
            Bulk Assign Trainees to Cluster
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#636e72' }}>
            Filter leads profiled &amp; surveyed trainees, select multiple candidates, and assign them directly to a Project &amp; Cluster.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={fetchAllData}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#fff',
              border: '1px solid #dfe6e9',
              borderRadius: '6px',
              color: '#2d3436',
              fontSize: '13px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
            {loading ? 'Refreshing...' : 'Refresh List'}
          </button>

          <Link
            to="/clusters"
            style={{
              padding: '8px 16px',
              backgroundColor: '#fff',
              border: '1px solid #dfe6e9',
              borderRadius: '6px',
              color: '#2d3436',
              fontSize: '13px',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            View Clusters
          </Link>
        </div>
      </div>

      {/* Top Search & Filter Bar (Matching Screenshot 2) */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '16px 20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by Name/Mobile/Location..."
              value={searchText}
              onChange={e => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                padding: '9px 14px',
                border: '1px solid #dfe6e9',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            onClick={() => setShowFilterModal(true)}
            style={{
              padding: '9px 18px',
              backgroundColor: '#ff8701',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i className="fa fa-filter"></i>
            Filter {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}
          </button>

          <button
            onClick={handleResetFilters}
            style={{
              padding: '9px 18px',
              backgroundColor: '#ff8701',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Reset
          </button>

          <button
            onClick={handleExportCSV}
            style={{
              padding: '9px 18px',
              backgroundColor: '#ff8701',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Export CSV <i className="fa fa-download"></i>
          </button>
        </div>

        {/* Orange Stat Chips (Matching Screenshot 2) */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginTop: '16px',
            paddingTop: '14px',
            borderTop: '1px solid #f1f2f6',
          }}
        >
          <span
            style={{
              backgroundColor: '#ff8701',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Total Profiles: {searchText ? filteredLeads.length : stats.total}
          </span>

          <span
            onClick={() => {
              setFilters(f => ({ ...f, stage: f.stage === 'basic' ? '' : 'basic' }));
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: '#ff8701',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              border: filters.stage === 'basic' ? '2px solid #2d3436' : 'none',
            }}
            title="Click to filter Basic profiles"
          >
            Basic Profiles: {searchText ? filteredLeads.filter(x => x.stage === 'basic').length : stats.basic}
          </span>

          <span
            onClick={() => {
              setFilters(f => ({ ...f, stage: f.stage === 'advance' ? '' : 'advance' }));
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: '#ff8701',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              border: filters.stage === 'advance' ? '2px solid #2d3436' : 'none',
            }}
            title="Click to filter Advanced profiles"
          >
            Advanced Profiles: {searchText ? filteredLeads.filter(x => x.stage === 'advance').length : stats.advance}
          </span>

          <span
            onClick={() => {
              setFilters(f => ({
                ...f,
                clusterStatus: f.clusterStatus === 'assigned' ? 'all' : 'assigned',
              }));
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: '#ff8701',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              border: filters.clusterStatus === 'assigned' ? '2px solid #2d3436' : 'none',
            }}
            title="Click to filter Assigned profiles"
          >
            Cluster Assigned: {searchText ? filteredLeads.filter(x => x.clusterName).length : stats.assigned}
          </span>

          <span
            onClick={() => {
              setFilters(f => ({
                ...f,
                clusterStatus: f.clusterStatus === 'unassigned' ? 'all' : 'unassigned',
              }));
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: filters.clusterStatus === 'unassigned' ? '#d63031' : '#e17055',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              border: filters.clusterStatus === 'unassigned' ? '2px solid #2d3436' : 'none',
            }}
            title="Click to view Unassigned profiles ready for assignment"
          >
            ⚡ Unassigned to Cluster: {searchText ? filteredLeads.filter(x => !x.clusterName).length : stats.unassigned}
          </span>
        </div>

        {/* Active Filter Tags */}
        {activeFiltersCount > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
              marginTop: '12px',
              paddingTop: '10px',
              borderTop: '1px dashed #dfe6e9',
            }}
          >
            <span style={{ fontSize: '12px', color: '#636e72', fontWeight: '600' }}>Active Filters:</span>

            {filters.state && (
              <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                State: <b>{filters.state}</b>
                <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, state: '' }))}></i>
              </span>
            )}

            {filters.district && (
              <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                District: <b>{filters.district}</b>
                <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, district: '' }))}></i>
              </span>
            )}

            {filters.block && (
              <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Block: <b>{filters.block}</b>
                <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, block: '' }))}></i>
              </span>
            )}

              {
                filters.village && (Array.isArray(filters.village) ? filters.village.length > 0 : Boolean(filters.village)) && (
                  <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Village: <b>{Array.isArray(filters.village) ? filters.village.map(v => typeof v === 'object' ? v.label || v.value : v).join(', ') : filters.village}</b>
                    <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, village: [] }))}></i>
                  </span>
                )
              }

            {filters.projectName && (
              <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Project: <b>{filters.projectName}</b>
                <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, projectName: '' }))}></i>
              </span>
            )}

            {filters.clusterStatus !== 'all' && (
              <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Status: <b>{filters.clusterStatus === 'assigned' ? 'Assigned' : 'Unassigned Only'}</b>
                <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, clusterStatus: 'all' }))}></i>
              </span>
            )}

            {filters.gender && (
              <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Gender: <b>{filters.gender}</b>
                <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, gender: '' }))}></i>
              </span>
            )}

            {filters.stage && (
              <span style={{ background: '#f1f2f6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Stage: <b>{filters.stage === 'advance' ? 'Advanced' : 'Basic'}</b>
                <i className="fa fa-times" style={{ cursor: 'pointer', color: '#ff8701' }} onClick={() => setFilters(f => ({ ...f, stage: '' }))}></i>
              </span>
            )}

            <button
              onClick={handleResetFilters}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#d63031',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Target Project & Cluster Bulk Assignment Sticky Action Bar */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '16px 20px',
          boxShadow: selectedIds.size > 0 ? '0 4px 16px rgba(255, 135, 1, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
          border: selectedIds.size > 0 ? '2px solid #ff8701' : '1px solid #dfe6e9',
          marginBottom: '16px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '6px 14px',
              backgroundColor: selectedIds.size > 0 ? '#ff8701' : '#dfe6e9',
              color: selectedIds.size > 0 ? '#fff' : '#636e72',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '700',
            }}
          >
            {selectedIds.size} Selected
          </span>

          <span style={{ fontSize: '13px', color: '#636e72' }}>
            Select trainees from the table below, choose a target Project &amp; Cluster, and click Assign:
          </span>

          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={handleSelectPage}
              style={{
                padding: '4px 10px',
                backgroundColor: '#f1f2f6',
                border: '1px solid #dfe6e9',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#2d3436',
                cursor: 'pointer',
              }}
            >
              Select Page ({paginatedLeads.length})
            </button>

            <button
              onClick={handleSelectAllFiltered}
              style={{
                padding: '4px 10px',
                backgroundColor: '#f1f2f6',
                border: '1px solid #dfe6e9',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#2d3436',
                cursor: 'pointer',
              }}
            >
              Select All Filtered ({filteredLeads.length})
            </button>

            {selectedIds.size > 0 && (
              <button
                onClick={handleClearSelection}
                style={{
                  padding: '4px 10px',
                  backgroundColor: '#fff',
                  border: '1px solid #ff7675',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#d63031',
                  cursor: 'pointer',
                }}
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Target Project Dropdown */}
          <select
            value={targetProjectId}
            onChange={e => {
              setTargetProjectId(e.target.value);
              setTargetClusterId('');
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid #dfe6e9',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              backgroundColor: '#fff',
              minWidth: '180px',
            }}
          >
            <option value="">1. Select Project</option>
            {(projectsList || []).map(proj => {
              if (!proj || !proj._id) return null;
              return (
                <option key={proj._id} value={proj._id}>
                  {proj.name || 'Unnamed Project'}
                </option>
              );
            })}
          </select>

          {/* Target Cluster Dropdown */}
          <select
            value={targetClusterId}
            onChange={e => setTargetClusterId(e.target.value)}
            disabled={!targetProjectId}
            style={{
              padding: '8px 12px',
              border: '1px solid #dfe6e9',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              backgroundColor: !targetProjectId ? '#f1f2f6' : '#fff',
              minWidth: '180px',
              cursor: !targetProjectId ? 'not-allowed' : 'pointer',
            }}
          >
            <option value="">
              {!targetProjectId
                ? 'Select Project First'
                : availableTargetClusters.length === 0
                  ? 'No Clusters In Project'
                  : '2. Select Cluster'}
            </option>
            {(availableTargetClusters || []).map(cl => {
              if (!cl || !cl._id) return null;
              return (
                <option key={cl._id} value={cl._id}>
                  {cl.clusterName || 'Unnamed Cluster'}
                </option>
              );
            })}
          </select>

          {/* Assign Button */}
          <button
            onClick={handleAssignToCluster}
            disabled={isAssigning || selectedIds.size === 0 || !targetProjectId || !targetClusterId}
            style={{
              padding: '9px 20px',
              backgroundColor:
                selectedIds.size > 0 && targetProjectId && targetClusterId ? '#ff8701' : '#b2bec3',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700',
              cursor:
                selectedIds.size > 0 && targetProjectId && targetClusterId && !isAssigning
                  ? 'pointer'
                  : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow:
                selectedIds.size > 0 && targetProjectId && targetClusterId
                  ? '0 2px 6px rgba(255, 135, 1, 0.4)'
                  : 'none',
            }}
          >
            <i className="fa fa-bolt"></i>
            {isAssigning ? `Assigning (${assignProgress}%)...` : 'Assign to Cluster'}
          </button>
        </div>
      </div>

      {/* Leads Table Container (Matching Screenshot 2 & 3) */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          overflow: 'hidden',
          border: '1px solid #dfe6e9',
        }}
      >
        <div style={{ overflowX: 'auto', maxHeight: '650px' }}>
          <table
            style={{
              width: '100%',
              minWidth: '1550px',
              borderCollapse: 'collapse',
              fontSize: '13px',
              textAlign: 'left',
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: '#fafafa',
                  borderBottom: '2px solid #dfe6e9',
                  color: '#2d3436',
                  fontWeight: '600',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                }}
              >
                <th style={{ width: '40px', padding: '12px 14px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isAllPageSelected}
                    onChange={e => {
                      if (e.target.checked) handleSelectPage();
                      else {
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          paginatedLeads.forEach(l => next.delete(l._id));
                          return next;
                        });
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ width: '50px', padding: '12px 8px' }}>S.No.</th>
                <th style={{ width: '180px', padding: '12px 10px' }}>Name</th>
                <th style={{ width: '160px', padding: '12px 10px' }}>Surveyed By</th>
                <th style={{ width: '100px', padding: '12px 10px' }}>Stage</th>
                <th style={{ width: '80px', padding: '12px 10px' }}>Gender</th>
                <th style={{ width: '60px', padding: '12px 10px' }}>Age</th>
                <th style={{ width: '120px', padding: '12px 10px' }}>Mobile No.</th>
                <th style={{ width: '130px', padding: '12px 10px' }}>State</th>
                <th style={{ width: '150px', padding: '12px 10px' }}>District</th>
                <th style={{ width: '150px', padding: '12px 10px' }}>Village</th>
                <th style={{ width: '140px', padding: '12px 10px' }}>Block</th>
                <th style={{ width: '140px', padding: '12px 10px' }}>Aadhaar Number</th>
                <th style={{ width: '120px', padding: '12px 10px' }}>SRLM Associated</th>
                <th style={{ width: '110px', padding: '12px 10px' }}>Apiary</th>
                <th style={{ width: '180px', padding: '12px 10px' }}>Project Name</th>
                <th style={{ width: '180px', padding: '12px 10px' }}>Cluster Name</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={17} style={{ padding: '60px', textAlign: 'center', color: '#636e72' }}>
                    <i className="fa fa-spinner fa-spin fa-2x" style={{ color: '#ff8701', marginBottom: '10px' }}></i>
                    <div>Loading Baseline and Advanced survey profiles...</div>
                  </td>
                </tr>
              ) : paginatedLeads.length === 0 ? (
                <tr>
                  <td colSpan={17} style={{ padding: '60px', textAlign: 'center', color: '#636e72' }}>
                    <i className="fa fa-search fa-2x" style={{ color: '#b2bec3', marginBottom: '10px' }}></i>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#2d3436' }}>
                      No trainees found matching the selected filters.
                    </div>
                    <div style={{ fontSize: '13px', marginTop: '4px' }}>
                      Try clearing search text or resetting the filter options.
                    </div>
                    <button
                      onClick={handleResetFilters}
                      style={{
                        marginTop: '12px',
                        padding: '6px 14px',
                        backgroundColor: '#ff8701',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      Reset Filters
                    </button>
                  </td>
                </tr>
              ) : (
                paginatedLeads.map((item, idx) => {
                  const u = item.userInfo || {};
                  const addr = u.address || {};
                  const fv = item.fieldViewData?.[0] || {};
                  const dobVal = item.surveyData?.stages?.[0]?.form_data?.dob;
                  const age = calculateAge(dobVal);
                  const isSelected = selectedIds.has(item._id);
                  const globalIndex = (currentPage - 1) * pageSize + idx + 1;

                  return (
                    <tr
                      key={item._id}
                      style={{
                        borderBottom: '1px solid #f1f2f6',
                        backgroundColor: isSelected ? '#fff9f4' : idx % 2 === 0 ? '#fff' : '#fafafa',
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Checkbox */}
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectOne(item._id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>

                      {/* S.No. */}
                      <td style={{ padding: '10px 8px', color: '#636e72' }}>{globalIndex}</td>

                      {/* Name with link and eye icon */}
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <a
                            href={`/user-profile/${u._id || item._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#0984e3',
                              textDecoration: 'none',
                              fontWeight: '600',
                            }}
                          >
                            {u.name || 'Unnamed Trainee'}
                          </a>
                          <span
                            onClick={() => setQuickViewUser(item)}
                            title="Quick View Details"
                            style={{
                              color: '#ff8701',
                              cursor: 'pointer',
                              padding: '2px 4px',
                              marginLeft: '6px',
                            }}
                          >
                            <i className="fa fa-eye"></i>
                          </span>
                        </div>
                      </td>

                      {/* Surveyor */}
                      <td style={{ padding: '10px 10px', color: '#2d3436' }}>
                        {item.surveyorName || '-'}
                      </td>

                      {/* Stage Badge */}
                      <td style={{ padding: '10px 10px' }}>
                        <span
                          style={{
                            padding: '3px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: item.stage === 'advance' ? '#e8f4fd' : '#fef5e7',
                            color: item.stage === 'advance' ? '#0984e3' : '#d35400',
                            border: `1px solid ${item.stage === 'advance' ? '#74b9ff' : '#f39c12'}`,
                          }}
                        >
                          {item.stage === 'advance' ? 'Advanced' : 'Basic'}
                        </span>
                      </td>

                      {/* Gender */}
                      <td style={{ padding: '10px 10px', color: '#2d3436' }}>
                        {typeof u.gender === 'string' && u.gender.trim()
                          ? u.gender.charAt(0).toUpperCase() + u.gender.slice(1).toLowerCase()
                          : '-'}
                      </td>

                      {/* Age */}
                      <td style={{ padding: '10px 10px', color: '#636e72' }}>{age}</td>

                      {/* Mobile */}
                      <td style={{ padding: '10px 10px', fontWeight: '500' }}>
                        {u.mobileNumber || '-'}
                      </td>

                      {/* State */}
                      <td style={{ padding: '10px 10px' }}>
                        {getLeadLocation(item).state || '-'}
                      </td>

                      {/* District */}
                      <td style={{ padding: '10px 10px' }}>
                        {getLeadLocation(item).district || '-'}
                      </td>

                      {/* Village */}
                      <td style={{ padding: '10px 10px' }}>
                        {getLeadLocation(item).village || '-'}
                      </td>

                      {/* Block */}
                      <td style={{ padding: '10px 10px' }}>
                        {getLeadLocation(item).block || '-'}
                      </td>

                      {/* Aadhaar Number */}
                      <td style={{ padding: '10px 10px', color: '#636e72' }}>
                        {item.aadhar_number || '-'}
                      </td>

                      {/* SRLM Associated */}
                      <td style={{ padding: '10px 10px' }}>
                        {item.srlm_associated === true ? 'Yes' : item.srlm_associated === false ? 'No' : '-'}
                      </td>

                      {/* Apiary Status */}
                      <td style={{ padding: '10px 10px' }}>
                        {item.apiaryStatus === 'Active' ? (
                          <span style={{ backgroundColor: '#e6fffa', color: '#00b894', border: '1px solid #b2f2bb', padding: '4px 8px', borderRadius: '12px', fontWeight: '600', fontSize: '11px' }}>
                            🟢 Active
                          </span>
                        ) : item.apiaryStatus === 'Inactive' ? (
                          <span style={{ backgroundColor: '#fff5f5', color: '#d63031', border: '1px solid #ffa8a8', padding: '4px 8px', borderRadius: '12px', fontWeight: '600', fontSize: '11px' }}>
                            🔴 Inactive
                          </span>
                        ) : (
                          <span style={{ backgroundColor: '#f1f2f6', color: '#636e72', border: '1px solid #dfe6e9', padding: '4px 8px', borderRadius: '12px', fontWeight: '500', fontSize: '11px' }}>
                            ⚪ Absent
                          </span>
                        )}
                      </td>

                      {/* Project Name */}
                      <td style={{ padding: '10px 10px' }}>
                        {item.projectName ? (
                          <span style={{ color: '#00b894', fontWeight: '600' }}>{item.projectName}</span>
                        ) : (
                          <span style={{ color: '#d63031' }}>-</span>
                        )}
                      </td>

                      {/* Cluster Name */}
                      <td style={{ padding: '10px 10px' }}>
                        {item.clusterName ? (
                          <span style={{ color: '#00b894', fontWeight: '600' }}>{item.clusterName}</span>
                        ) : (
                          <span style={{ color: '#d63031' }}>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom Pagination Bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px',
            backgroundColor: '#fafafa',
            borderTop: '1px solid #dfe6e9',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#636e72' }}>
              Showing {filteredLeads.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, filteredLeads.length)} of {filteredLeads.length} profiles
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: '#636e72' }}>Rows per page:</span>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #dfe6e9',
                  fontSize: '12px',
                  outline: 'none',
                  backgroundColor: '#fff',
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #dfe6e9',
                backgroundColor: currentPage === 1 ? '#f1f2f6' : '#fff',
                color: currentPage === 1 ? '#b2bec3' : '#2d3436',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              &laquo; First
            </button>

            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #dfe6e9',
                backgroundColor: currentPage === 1 ? '#f1f2f6' : '#fff',
                color: currentPage === 1 ? '#b2bec3' : '#2d3436',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              &lsaquo; Prev
            </button>

            <span style={{ fontSize: '13px', padding: '0 8px', fontWeight: '600', color: '#2d3436' }}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #dfe6e9',
                backgroundColor: currentPage === totalPages ? '#f1f2f6' : '#fff',
                color: currentPage === totalPages ? '#b2bec3' : '#2d3436',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Next &rsaquo;
            </button>

            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #dfe6e9',
                backgroundColor: currentPage === totalPages ? '#f1f2f6' : '#fff',
                color: currentPage === totalPages ? '#b2bec3' : '#2d3436',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Last &raquo;
            </button>
          </div>
        </div>
      </div>

      {/* 1. Confirm Bulk Assignment Modal (Native React Modal) */}
      {showConfirmAssignModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '28px 32px',
              width: '90%',
              maxWidth: '520px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#fff4e6',
                color: '#ff8701',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '26px',
              }}
            >
              <i className="fa fa-users"></i>
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '700', color: '#2d3436' }}>
              Confirm Bulk Assignment
            </h3>

            <p style={{ margin: '0 0 16px', color: '#636e72', fontSize: '14px' }}>
              You are about to assign <b style={{ color: '#ff8701' }}>{selectedIds.size}</b> selected trainee(s) to:
            </p>

            <div
              style={{
                background: '#f8f9fa',
                border: '1px solid #dfe6e9',
                borderRadius: '8px',
                padding: '14px 18px',
                marginBottom: '16px',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                <span style={{ color: '#636e72', fontWeight: '600' }}>Project:</span>
                <span style={{ color: '#ff8701', fontWeight: '700' }}>{assignTargetNames.project}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: '#636e72', fontWeight: '600' }}>Cluster:</span>
                <span style={{ color: '#00b894', fontWeight: '700' }}>{assignTargetNames.cluster}</span>
              </div>
            </div>

            {/* Selected Trainees Preview List */}
            <div style={{ textAlign: 'left', marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '6px' }}>
                Selected Trainees ({selectedIds.size}):
              </div>
              <div
                style={{
                  maxHeight: '110px',
                  overflowY: 'auto',
                  border: '1px solid #f1f2f6',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  background: '#fafafa',
                  fontSize: '12px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                {Array.from(selectedIds).map(id => {
                  const item = allLeads.find(l => l._id === id);
                  const name = item?.userInfo?.name || item?.name || 'Trainee';
                  return (
                    <span
                      key={id}
                      style={{
                        padding: '3px 8px',
                        background: '#e8f4fd',
                        color: '#0984e3',
                        borderRadius: '12px',
                        fontWeight: '600',
                      }}
                    >
                      {name}
                    </span>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                onClick={() => setShowConfirmAssignModal(false)}
                style={{
                  padding: '10px 22px',
                  backgroundColor: '#dfe6e9',
                  color: '#2d3436',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeBulkAssign}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#ff8701',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 135, 1, 0.4)',
                }}
              >
                Yes, Assign {selectedIds.size} Trainees
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Live Assignment Progress & Results Modal (Native React Modal) */}
      {isAssigning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: '36px 32px',
              borderRadius: '12px',
              textAlign: 'center',
              width: '90%',
              maxWidth: '480px',
              boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
            }}
          >
            {/* Step A: Processing State */}
            {assignStep === 'processing' && (
              <>
                <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#ff8701', marginBottom: '18px' }}></i>
                <h3 style={{ margin: '0 0 6px', color: '#2d3436', fontSize: '20px', fontWeight: '700' }}>
                  Assigning Trainees to Cluster
                </h3>
                <p style={{ margin: '0 0 12px', color: '#636e72', fontSize: '13px' }}>
                  Target Cluster: <b style={{ color: '#00b894' }}>{assignTargetNames.cluster}</b> ({assignTargetNames.project})
                </p>

                <div style={{ margin: '18px 0 10px', fontSize: '14px', fontWeight: '600', color: '#2d3436' }}>
                  Processing {assignCurrent} of {assignTotal}: <span style={{ color: '#ff8701' }}>{assignCurrentName}</span> ({assignProgress}%)
                </div>

                <div
                  style={{
                    background: '#f1f2f6',
                    borderRadius: '10px',
                    height: '14px',
                    overflow: 'hidden',
                    width: '100%',
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      background: 'linear-gradient(90deg, #ff8701, #f39c12)',
                      height: '100%',
                      width: `${assignProgress}%`,
                      transition: 'width 0.25s ease',
                    }}
                  ></div>
                </div>

                <p style={{ margin: 0, fontSize: '12px', color: '#b2bec3' }}>
                  Updating field view &amp; base profile records in database. Please wait...
                </p>
              </>
            )}

            {/* Step B: All Succeeded State */}
            {assignStep === 'done' && (
              <>
                <i className="fa fa-check-circle fa-4x" style={{ color: '#00b894', marginBottom: '16px' }}></i>
                <h3 style={{ margin: '0 0 8px', color: '#2d3436', fontSize: '20px', fontWeight: '700' }}>
                  Assignment Complete! 🎉
                </h3>
                <p style={{ color: '#636e72', fontSize: '14px', margin: '0 0 16px', lineHeight: 1.5 }}>
                  Successfully assigned all <b style={{ color: '#00b894' }}>{assignResults.successCount}</b> trainee(s) to cluster{' '}
                  <b style={{ color: '#00b894' }}>{assignTargetNames.cluster}</b> in <b style={{ color: '#ff8701' }}>{assignTargetNames.project}</b>.
                </p>
                <button
                  onClick={handleCloseAssignModal}
                  style={{
                    padding: '10px 28px',
                    backgroundColor: '#00b894',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 184, 148, 0.4)',
                  }}
                >
                  Done &amp; Refresh Table
                </button>
              </>
            )}

            {/* Step C: Partial / Error State */}
            {(assignStep === 'partial' || assignStep === 'error') && (
              <>
                <i
                  className="fa fa-exclamation-triangle fa-3x"
                  style={{ color: assignStep === 'error' ? '#d63031' : '#f39c12', marginBottom: '16px' }}
                ></i>
                <h3 style={{ margin: '0 0 8px', color: '#2d3436', fontSize: '20px', fontWeight: '700' }}>
                  {assignStep === 'error' ? 'Assignment Failed' : 'Assignment Completed with Warnings'}
                </h3>
                <div style={{ textAlign: 'left', fontSize: '13px', margin: '0 0 16px', lineHeight: 1.5 }}>
                  <p style={{ margin: '0 0 6px' }}>
                    <b>Successfully assigned:</b>{' '}
                    <span style={{ color: '#00b894', fontWeight: '700' }}>{assignResults.successCount} trainee(s)</span>
                  </p>
                  <p style={{ margin: '0 0 10px', color: '#d63031' }}>
                    <b>Failed:</b> <span style={{ fontWeight: '700' }}>{assignResults.failCount} trainee(s)</span>
                  </p>
                  {assignResults.errorDetails.length > 0 && (
                    <div
                      style={{
                        background: '#fff5f5',
                        border: '1px solid #ffa8a8',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        maxHeight: '140px',
                        overflowY: 'auto',
                        fontSize: '12px',
                        color: '#c0392b',
                      }}
                    >
                      {assignResults.errorDetails.map((errText, idx) => (
                        <div key={idx} style={{ marginBottom: '4px' }}>
                          • {errText}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleCloseAssignModal}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#636e72',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  Close &amp; Refresh
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Filter Modal (Exact Recreation of Screenshot 4) */}
      {showFilterModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
          }}
        >
          <div
            style={{
              width: '95%',
              maxWidth: '480px',
              maxHeight: '90vh',
              background: '#fff',
              borderRadius: '12px',
              padding: '24px',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
              position: 'relative',
            }}
          >
            {/* Top Close Button (Orange Circle X from Screenshot 4) */}
            <div
              onClick={() => setShowFilterModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                color: '#fff',
                backgroundColor: '#ef5a20',
                height: '32px',
                width: '32px',
                fontWeight: 'bold',
                fontSize: '15px',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(239, 90, 32, 0.4)',
              }}
            >
              &#10005;
            </div>

            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '700', color: '#2d3436' }}>
              Filter Profiles
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* State Filter */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                  State
                </label>
                <select
                  value={filters.state}
                  onChange={e => {
                    const newState = e.target.value;
                    setFilters(f => ({ ...f, state: newState, district: '', block: '', village: [] }));
                  }}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                >
                  <option value="">Select State</option>
                  {locationOptions.states.map(st => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </div>

              {/* District Filter */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                  District
                </label>
                <select
                  value={filters.district}
                  onChange={e => {
                    const newDistrict = e.target.value;
                    setFilters(f => ({ ...f, district: newDistrict, block: '', village: [] }));
                  }}
                  disabled={!filters.state}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    border: '1px solid #dfe6e9',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none',
                    backgroundColor: !filters.state ? '#f1f2f6' : '#fff',
                    color: !filters.state ? '#b2bec3' : '#2d3436',
                  }}
                >
                  <option value="">{filters.state ? 'Select District' : 'SELECT STATE FIRST'}</option>
                  {locationOptions.districts.map(dt => (
                    <option key={dt} value={dt}>
                      {dt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Block Filter */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                  Block
                </label>
                <select
                  value={filters.block}
                  onChange={e => {
                    const newBlock = e.target.value;
                    setFilters(f => ({ ...f, block: newBlock, village: [] }));
                  }}
                  disabled={!filters.district}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    border: '1px solid #dfe6e9',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none',
                    backgroundColor: !filters.district ? '#f1f2f6' : '#fff',
                    color: !filters.district ? '#b2bec3' : '#2d3436',
                  }}
                >
                  <option value="">{filters.district ? 'Select Block' : 'SELECT DISTRICT FIRST'}</option>
                  {locationOptions.blocks.map(bk => (
                    <option key={bk} value={bk}>
                      {bk}
                    </option>
                  ))}
                </select>
              </div>

              {/* Village Filter (Multi-select like Lead Profile) */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                  Village {Array.isArray(filters.village) && filters.village.length > 0 ? `(${filters.village.length} selected)` : ''}
                </label>
                    <Select
                      isMulti
                      isClearable
                      placeholder={filters.district ? 'Select Village(s)...' : 'SELECT DISTRICT FIRST'}
                      isDisabled={!filters.district}
                      options={locationOptions.villages.map(vg => ({ value: vg, label: vg }))}
                      value={
                        Array.isArray(filters.village)
                          ? filters.village.map(v => typeof v === 'object' ? v : { value: v, label: v })
                          : filters.village
                            ? [{ value: filters.village, label: filters.village }]
                            : []
                      }
                      onChange={selected => {
                        const values = selected ? selected.map(s => s.value) : [];
                        setFilters(f => ({ ...f, village: values }));
                      }}
                        styles={{
                    control: (base, state) => ({
                  ...base,
                  minHeight: '38px',
                fontSize: '13px',
                borderRadius: '6px',
                borderColor: '#dfe6e9',
                backgroundColor: !filters.district ? '#f1f2f6' : '#fff',
                boxShadow: state.isFocused ? '0 0 0 1px #ff8701' : 'none',
                '&:hover': {
                  borderColor: '#ff8701',
                      },
                    }),
                    menu: base => ({
                  ...base,
                  fontSize: '13px',
                zIndex: 99999,
                    }),
                    menuPortal: base => ({...base, zIndex: 99999 }),
                    multiValue: base => ({
                  ...base,
                  backgroundColor: '#fff3e0',
                borderRadius: '4px',
                    }),
                    multiValueLabel: base => ({
                  ...base,
                  color: '#d35400',
                fontWeight: 600,
                fontSize: '12px',
                    }),
                    multiValueRemove: base => ({
                  ...base,
                  color: '#d35400',
                ':hover': {
                  backgroundColor: '#ff8701',
                color: 'white',
                      },
                    }),
                  }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
            </div>

            {/* Project Name Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                Project Name
              </label>
              <select
                value={filters.projectName}
                onChange={e => {
                  const val = e.target.value;
                  setFilters(f => ({ ...f, projectName: val }));
                }}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              >
                <option value="">Select Project Name</option>
                {projectsList.map(proj => (
                  <option key={proj._id} value={proj.name}>
                    {proj.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Cluster Status Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                Cluster Assignment Status
              </label>
              <select
                value={filters.clusterStatus}
                onChange={e => {
                  const val = e.target.value;
                  setFilters(f => ({ ...f, clusterStatus: val }));
                }}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              >
                <option value="all">All (Assigned &amp; Unassigned)</option>
                <option value="unassigned">&#9888; Unassigned to Cluster Only</option>
                <option value="assigned">&check; Assigned to Cluster Only</option>
              </select>
            </div>

            {/* Gender Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                Gender
              </label>
              <select
                value={filters.gender}
                onChange={e => {
                  const val = e.target.value;
                  setFilters(f => ({ ...f, gender: val }));
                }}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* SRLM Associated */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                SRLM Associated
              </label>
              <select
                value={filters.srlm_associated}
                onChange={e => {
                  const val = e.target.value;
                  setFilters(f => ({ ...f, srlm_associated: val }));
                }}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              >
                <option value="">Select SRLM Status</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>

            {/* Apiary Status */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                Apiary Status
              </label>
              <select
                value={filters.apiaryStatus}
                onChange={e => {
                  const val = e.target.value;
                  setFilters(f => ({ ...f, apiaryStatus: val }));
                }}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              >
                <option value="">Select Apiary Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Absent">Absent</option>
              </select>
            </div>

            {/* Stage Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#636e72', marginBottom: '4px' }}>
                Survey Stage
              </label>
              <select
                value={filters.stage}
                onChange={e => {
                  const val = e.target.value;
                  setFilters(f => ({ ...f, stage: val }));
                }}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              >
                <option value="">All Stages</option>
                <option value="advance">Advanced (Baseline Survey)</option>
                <option value="basic">Basic Profile</option>
              </select>
            </div>
          </div>

          {/* Modal Bottom Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '14px', borderTop: '1px solid #f1f2f6' }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f1f2f6',
                color: '#2d3436',
                border: '1px solid #dfe6e9',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Reset All
            </button>

            <button
              onClick={() => {
                setShowFilterModal(false);
                setCurrentPage(1);
              }}
              style={{
                padding: '8px 20px',
                backgroundColor: '#ff8701',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
        </div>
  )
}

{/* Quick View Trainee Details Modal */ }
{
  quickViewUser && (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99999,
      }}
    >
      <div
        style={{
          width: '95%',
          maxWidth: '520px',
          maxHeight: '90vh',
          background: '#fff',
          borderRadius: '12px',
          padding: '24px',
          overflowY: 'auto',
          boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
          position: 'relative',
        }}
      >
        <div
          onClick={() => setQuickViewUser(null)}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            color: '#fff',
            backgroundColor: '#ef5a20',
            height: '32px',
            width: '32px',
            fontWeight: 'bold',
            fontSize: '15px',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          &#10005;
        </div>

        <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '700', color: '#2d3436' }}>
          {quickViewUser.userInfo?.name || 'Trainee Profile'}
        </h3>
        <p style={{ margin: '0 0 16px', color: '#636e72', fontSize: '13px' }}>
          Stage: <b>{quickViewUser.stage === 'advance' ? 'Advanced (Baseline Survey)' : 'Basic Profile'}</b>
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
          <div>
            <span style={{ color: '#636e72' }}>Mobile:</span>
            <div style={{ fontWeight: '600' }}>{quickViewUser.userInfo?.mobileNumber || '-'}</div>
          </div>

          <div>
            <span style={{ color: '#636e72' }}>Gender:</span>
            <div style={{ fontWeight: '600' }}>{quickViewUser.userInfo?.gender || '-'}</div>
          </div>

          <div>
            <span style={{ color: '#636e72' }}>Aadhaar:</span>
            <div style={{ fontWeight: '600' }}>{quickViewUser.aadhar_number || '-'}</div>
          </div>

          <div>
            <span style={{ color: '#636e72' }}>Age:</span>
            <div style={{ fontWeight: '600' }}>
              {calculateAge(quickViewUser.surveyData?.stages?.[0]?.form_data?.dob)}
            </div>
          </div>

          <div>
            <span style={{ color: '#636e72' }}>State:</span>
            <div style={{ fontWeight: '600' }}>
              {getLeadLocation(quickViewUser).state || '-'}
            </div>
          </div>

          <div>
            <span style={{ color: '#636e72' }}>District:</span>
            <div style={{ fontWeight: '600' }}>
              {getLeadLocation(quickViewUser).district || '-'}
            </div>
          </div>

          <div>
            <span style={{ color: '#636e72' }}>Block:</span>
            <div style={{ fontWeight: '600' }}>
              {getLeadLocation(quickViewUser).block || '-'}
            </div>
          </div>

          <div>
            <span style={{ color: '#636e72' }}>Village:</span>
            <div style={{ fontWeight: '600' }}>
              {getLeadLocation(quickViewUser).village || '-'}
            </div>
          </div>

          <div style={{ gridColumn: 'span 2', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
            <span style={{ color: '#636e72', display: 'block', marginBottom: '4px' }}>Cluster Association:</span>
            <div>
              Project: <b>{quickViewUser.projectName || 'None'}</b>
            </div>
            <div>
              Cluster: <b>{quickViewUser.clusterName || 'None'}</b>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <a
            href={`/user-profile/${quickViewUser.userInfo?._id || quickViewUser._id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '8px 16px',
              backgroundColor: '#ff8701',
              color: '#fff',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              textDecoration: 'none',
            }}
          >
            Open Full Profile &rarr;
          </a>
        </div>
      </div>
    </div>
  )
}
    </div >
  );
};

// Error Boundary to ensure the page NEVER crashes into a blank white screen
class BulkAssignErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('BulkAssignTrainees boundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            marginLeft: '15%',
            marginTop: '5%',
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#fff',
            borderRadius: '8px',
            margin: '6% 4% 4% 18%',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
        >
          <i className="fa fa-exclamation-triangle" style={{ fontSize: '40px', color: '#e74c3c', marginBottom: '16px' }}></i>
          <h2 style={{ color: '#2d3436', margin: '0 0 8px' }}>Something went wrong rendering the table</h2>
          <p style={{ color: '#636e72', fontSize: '14px', maxWidth: '500px', margin: '0 auto 12px' }}>
            A rendering error occurred while filtering or displaying trainees. Click below to reload cleanly.
          </p>
          {this.state.error && (
            <div style={{ background: '#fff5f5', border: '1px solid #ffa8a8', color: '#c0392b', padding: '10px 14px', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace', maxWidth: '650px', margin: '0 auto 16px', textAlign: 'left', wordBreak: 'break-word' }}>
              <b>Error:</b> {this.state.error.message || String(this.state.error)}
              {this.state.error.stack && (
                <div style={{ marginTop: '8px', fontSize: '11px', maxHeight: '100px', overflowY: 'auto', whiteSpace: 'pre-wrap', color: '#7f1d1d' }}>
                  {this.state.error.stack}
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = '/clusters/assign-trainees';
              }}
              style={{
                padding: '10px 24px',
                backgroundColor: '#ff8701',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '13px',
              }}
            >
              Reset & Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const BulkAssignTraineesWrapper = (props) => (
  <BulkAssignErrorBoundary>
    <BulkAssignTrainees {...props} />
  </BulkAssignErrorBoundary>
);

export default BulkAssignTraineesWrapper;

